import { AdvancedSelect } from '../../../../../widgets/select';
import { Button } from '../../../../../widgets/buttons';
import { Checkbox } from '../../../../../widgets/checkbox';
import { ColorPicker } from '../../../../../widgets/colorPicker';
import { FormControl } from '../../../../../widgets/formControl';
import { Input } from '../../../../../widgets/input';
import { useDialogManager } from '../../../../../widgets/dialog';
import React, { FC, useCallback, useEffect, useState } from 'react';
import Slider from 'src/components/widgets/slider';
import { UpgradeDialog } from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import CubeMapIcon from '../icons/cubeMapIcon.svg';

interface SettingsEditorProps {
    sceneEditor: any;
    zkScene: any;
    isAIEnabled: boolean;
    setAIEnabled: (enabled: boolean) => void;
    additionalFeatures?: any; // Aggiungi questa prop
}

const Container = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
`;

const Column = styled.div`
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
    padding: 4px 0;
`;

const IndentedColumn = styled(Column)`
    padding-left: 24px;
`;

const DoubleIndentedColumn = styled(Column)`
    padding-left: 48px;
`;

const Row = styled.div<{ $indented?: number | boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    margin-left: ${(props) => {
        if (typeof props.$indented === 'number') {
            return `${props.$indented * 24}px`;
        }
        return props.$indented ? '24px' : '0';
    }};
`;

const Label = styled.label`
    font-size: 13px;
    color: #555;
    min-width: 120px;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const NumberInputStyled = styled(Input)`
    width: 80px;
`;

const ColorPickerContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ColorPreviewContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
`;

const ColorPreview = styled.div<{ color: string }>`
    width: 32px;
    height: 24px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: ${(props) => props.color};
    cursor: pointer;
    position: relative;
`;

const ColorHex = styled.span`
    font-family: monospace;
    font-size: 12px;
    color: #666;
    min-width: 70px;
    cursor: pointer;
    padding: 4px 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
`;

const ColorPickerPopover = styled.div`
    position: absolute;
    top: 100%;
    right: -50px;
    z-index: 1000;
    margin-top: 4px;
`;

const PreviewContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const PreviewImage = styled.img`
    max-width: 100px;
    max-height: 100px;
    border: 1px solid #ddd;
    border-radius: 4px;
`;

const PreviewIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #f5f5f5;
    font-size: 12px;
    font-weight: bold;
    color: #666;

    &.cubemap {
        font-size: 20px;
    }

    i {
        font-size: 24px;
    }
`;

const TexturePreviewContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const SliderContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex: 1;
`;

const SliderValueDisplay = styled.span`
    font-size: 11px;
    color: #666;
    min-width: 70px;
    text-align: right;
    font-family: monospace;
`;

const UpgradeBanner = styled(UpgradeDialog)`
    margin-bottom: 20px;
    p {
        margin: 0;
        padding: 0;
        line-height: 18px;
    }
`;

// Helper function per ottenere gli URL delle texture come nel vecchio Settings
const getTextureUrls = (texture: any): string[] => {
    if (!texture) return [];

    // Se è già un array di stringhe (files dalla configurazione)
    if (Array.isArray(texture) && texture.length > 0 && typeof texture[0] === 'string') {
        return texture;
    }

    // Se è una texture Babylon con _texture
    if (texture._texture?.url) {
        return [texture._texture.url];
    }

    // Se ha direttamente un url
    if (texture.url) {
        return [texture.url];
    }

    // Se è un array di URL (cubemap)
    if (Array.isArray(texture)) {
        const urls = texture.map((t) => t.url || t._texture?.url).filter(Boolean);
        return urls;
    }

    // Se ha una proprietà files (cubemap)
    if (texture.files && Array.isArray(texture.files)) {
        return texture.files;
    }

    console.log('No URLs found, returning empty array');
    return [];
};

// Helper per la preview delle texture come nel vecchio Settings
const getPreview = (urls: string | string[] | null | undefined) => {
    if (!urls) return null;

    // Converti in array se è una stringa
    const urlArray = Array.isArray(urls) ? urls : [urls];
    if (urlArray.length === 0) return null;

    let preview: React.ReactElement;

    if (urlArray[0].endsWith('.hdr')) {
        preview = (
            <PreviewIcon>
                <span>HDR</span>
            </PreviewIcon>
        );
    } else if (urlArray[0].endsWith('.dds')) {
        preview = (
            <PreviewIcon>
                <span>DDS</span>
            </PreviewIcon>
        );
    } else if (urlArray[0].endsWith('.env')) {
        preview = (
            <PreviewIcon>
                <span>ENV</span>
            </PreviewIcon>
        );
    } else if (urlArray.length > 1 || (urlArray.length === 1 && !urlArray[0].split('/')?.pop()?.includes('.'))) {
        // Cubemap (più file)
        preview = (
            <PreviewIcon className='cubemap'>
                <CubeMapIcon />
            </PreviewIcon>
        );
    } else {
        // Immagine normale
        preview = <PreviewImage src={urlArray[0]} alt='Preview' />;
    }

    return preview;
};

const SettingsEditor: FC<SettingsEditorProps> = ({ sceneEditor, zkScene, isAIEnabled, setAIEnabled, additionalFeatures }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const { ecommerceName, sellerMarketplace, updateSubscription } = useSubscriptionData();

    // State for scene configuration
    const [environmentTexture, setEnvironmentTexture] = useState<any>(null);
    const [skyboxTexture, setSkyboxTexture] = useState<any>(null);
    const initialBlur = (zkScene?.getOrCreateSkyboxConfig()?.get('blur') ?? 0) * 100;
    const [skyboxBlur, setSkyboxBlur] = useState<number>(initialBlur);
    const [isEnvironmentTextureUploading, setIsEnvironmentTextureUploading] = useState<boolean>(false);
    const [isSkyboxTextureUploading, setIsSkyboxTextureUploading] = useState<boolean>(false);
    const [infoPointsBorderColor, setInfoPointsBorderColor] = useState<string>('#ffffff');
    const [infoPointsColor, setInfoPointsColor] = useState<string>('#ffffff');
    const [infoPointsHighlightColor, setInfoPointsHighlightColor] = useState<string>('#ff0000');
    const [infoPointsInnerPointColor, setInfoPointsInnerPointColor] = useState<string>('#000000');
    const [enableAntialiasing, setEnableAntialiasing] = useState<boolean>(true);
    const [antialiasDesktop, setAntialiasDesktop] = useState<boolean>(true);
    const [antialiasMobile, setAntialiasMobile] = useState<boolean>(true);
    const [antialiasDesktopInRotation, setAntialiasDesktopInRotation] = useState<boolean>(false);
    const [antialiasMobileInRotation, setAntialiasMobileInRotation] = useState<boolean>(false);
    const [webpCompressionType, setWebpCompressionType] = useState<string>('Lossy');
    const [webpCompressionQuality, setWebpCompressionQuality] = useState<number>(80);
    // const [dracoCompression, setDracoCompression] = useState<boolean>(false);
    const [enableAI, setEnableAI] = useState<boolean>(false);
    const [optimizeTextureManagement, setOptimizeTextureManagement] = useState<boolean>(false);
    const [enableWebP, setEnableWebP] = useState<boolean>(false);
    const [useDelayedMaterialsLoading, setUseDelayedMaterialsLoading] = useState<boolean>(false);
    const [infoPointsEnabled, setInfoPointsEnabled] = useState<boolean>(false);
    const [openColorPicker, setOpenColorPicker] = useState<string | null>(null);

    const getAntiAliasConfig = useCallback(() => {
        try {
            const antiAliasConfig = sceneEditor?.getSceneConfig()?.toJSON()?.general?.antialias;
            if (!antiAliasConfig) {
                return {
                    desktop: {
                        enabled: false,
                        inRotationEnabled: false
                    },
                    mobile: {
                        enabled: false,
                        inRotationEnabled: false
                    }
                };
            }
            return antiAliasConfig;
        } catch (error) {
            console.error('Error getting antialias config:', error);
            return {
                desktop: {
                    enabled: false,
                    inRotationEnabled: false
                },
                mobile: {
                    enabled: false,
                    inRotationEnabled: false
                }
            };
        }
    }, [sceneEditor]);

    const setAntiAliasConfig = useCallback(
        (config: any) => {
            try {
                const sceneConfig = sceneEditor?.getSceneConfig();
                if (sceneConfig) {
                    const generalConfig = sceneConfig.get('general');
                    if (generalConfig && (window as any).Zakeke?.SceneAntiAliasingConfig) {
                        generalConfig.set('antialias', new (window as any).Zakeke.SceneAntiAliasingConfig(config));
                    }
                }
            } catch (error) {
                console.error('Error setting antialias config:', error);
            }
        },
        [sceneEditor]
    );

    // Helper functions seguendo la logica del vecchio SceneEditorPage
    const setWebPEnabled = useCallback(
        (enabled: boolean) => {
            setEnableWebP(enabled);
            zkScene?.setWebPEnabled?.(enabled);
        },
        [zkScene]
    );

    const setWebPType = useCallback(
        (type: string) => {
            setWebpCompressionType(type);
            zkScene?.setWebPType?.(type);
        },
        [zkScene]
    );

    const setWebPQuality = useCallback(
        (quality: number) => {
            setWebpCompressionQuality(quality);
            zkScene?.setWebPQuality?.(quality);
        },
        [zkScene]
    );

    const setMatchSameNameTextures = useCallback(
        (enabled: boolean) => {
            setOptimizeTextureManagement(enabled);
            sceneEditor?.setMatchSameNameTextures?.(enabled);
        },
        [sceneEditor]
    );

    const setUseDelayedMaterialLoading = useCallback(
        (enabled: boolean) => {
            setUseDelayedMaterialsLoading(enabled);
            sceneEditor?.setUseDelayedMaterialLoading?.(enabled);
        },
        [sceneEditor]
    );

    const setInfoPointsEnabledHandler = useCallback(
        (enabled: boolean) => {
            setInfoPointsEnabled(enabled);
            sceneEditor!.setInfoPointsEnabled(enabled);
        },
        [sceneEditor, zkScene]
    );

    // Handler functions per environment e skybox seguendo il pattern del vecchio SceneEditorPage
    const handleEnvironmentTextureUpload = useCallback(() => {
        // Controllo delle additional features per customEnvironmentTexture
        if (!additionalFeatures?.customEnvironmentTexture) {
            showDialog(
                'Upgrade_Plan',
                <UpgradeBanner
                    marketPlace={sellerMarketplace ?? undefined}
                    message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'Grow')}
                    onUpgradeCompleted={async () => {
                        updateSubscription();
                    }}
                />
            );
            return;
        }

        const input = document.createElement('input');
        input.setAttribute('multiple', 'multiple');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', '.jpeg,.jpg,.png,.hdr,.dds,.env');
        input.addEventListener('change', async (e) => {
            const files = (e.currentTarget as HTMLInputElement).files;
            if (files && files.length > 0) {
                setIsEnvironmentTextureUploading(true);

                sceneEditor?.loadTextureFiles(
                    Array.from(files),
                    null,
                    'environment',
                    false,
                    () => {
                        setIsEnvironmentTextureUploading(false);
                        // Forza l'aggiornamento dello stato della texture dopo l'upload
                        setTimeout(() => {
                            setEnvironmentTexture(sceneEditor?.scene?.environmentTexture || true);
                        }, 100);
                    },
                    () => {
                        showDialog(
                            'error-dialog',
                            <div style={{ padding: '20px', textAlign: 'center' }}>
                                <h3>{T._('Upload failed', 'Admin')}</h3>
                                <p>{T._('Failed uploading texture.', 'Admin')}</p>
                                <Button onClick={() => closeDialog('error-dialog')}>{T._('Close', 'Admin')}</Button>
                            </div>
                        );
                        setIsEnvironmentTextureUploading(false);
                    }
                );
            }
        });
        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    }, [sceneEditor, showDialog, closeDialog, additionalFeatures]);

    const handleEnvironmentTextureRemove = useCallback(() => {
        sceneEditor?.removeSceneEnvironmentTexture();
        setEnvironmentTexture(null);
    }, [sceneEditor]);

    const handleSkyboxTextureUpload = useCallback(() => {
        // Controllo delle additional features per customEnvironmentTexture
        if (!additionalFeatures?.customEnvironmentTexture) {
            showDialog(
                'Upgrade_Plan',
                <UpgradeBanner
                    marketPlace={sellerMarketplace ?? undefined}
                    message={stringFormat(
                        T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'),
                        'Custom Environment Texture'
                    )}
                    onUpgradeCompleted={async () => {
                        updateSubscription();
                    }}
                />
            );
            return;
        }

        const input = document.createElement('input');
        input.setAttribute('multiple', 'multiple');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', '.jpeg,.jpg,.png,.hdr,.dds,.env');
        input.addEventListener('change', async (e) => {
            const files = (e.currentTarget as HTMLInputElement).files;
            if (files && files.length > 0) {
                const fileArray = Array.from(files);
                const fileName = fileArray[0].name.toLowerCase();

                // Validazione formato file come nel vecchio SceneEditorPage
                if (
                    fileName.endsWith('.hdr') ||
                    fileName.endsWith('.dds') ||
                    (fileName.endsWith('.jpg') && fileArray.length === 6) ||
                    fileName.endsWith('.env')
                ) {
                    setIsSkyboxTextureUploading(true);
                    // Attiva environment mode viewer come nel SceneEditorToolbar
                    sceneEditor?.setEnvironmentMode(2); // EnvironmentMode.Viewer = 2
                    sceneEditor?.setViewerSkyboxCreateSkybox(true);

                    sceneEditor?.loadTextureFiles(
                        fileArray,
                        null,
                        'skybox',
                        false,
                        () => {
                            setIsSkyboxTextureUploading(false);
                            // Forza l'aggiornamento dello stato della texture dopo l'upload
                            setTimeout(() => {
                                setSkyboxTexture(sceneEditor?.getSkyboxTexture?.() || true);
                            }, 100);
                        },
                        () => {
                            showDialog(
                                'error-dialog',
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                    <h3>{T._('Upload failed', 'Admin')}</h3>
                                    <p>{T._('Failed uploading texture.', 'Admin')}</p>
                                    <Button onClick={() => closeDialog('error-dialog')}>{T._('Close', 'Admin')}</Button>
                                </div>
                            );
                            setIsSkyboxTextureUploading(false);
                        }
                    );
                } else {
                    sceneEditor?.setViewerSkyboxCreateSkybox(false);
                    showDialog(
                        'format-error-dialog',
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <h3>{T._('Upload failed.', 'Admin')}</h3>
                            <p>{T._('The file must be in one of the following formats: .hdr, .dds, .env or 6 .jpg files (cubemap).', 'Admin')}</p>
                            <Button onClick={() => closeDialog('format-error-dialog')}>{T._('Close', 'Admin')}</Button>
                        </div>
                    );
                }
            }
        });
        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    }, [sceneEditor, showDialog, closeDialog]);

    const handleSkyboxTextureRemove = useCallback(() => {
        sceneEditor?.setViewerSkyboxCreateSkybox(false);
        sceneEditor?.removeViewerSkyboxTexture();
        setSkyboxTexture(null);
    }, [sceneEditor]);

    const setAiConfigurationEnabled = useCallback(
        (enabled: boolean) => {
            // Verifica se la feature AI Powered Tools è disponibile
            if (enabled && !additionalFeatures?.aiPoweredTools) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        marketPlace={sellerMarketplace ?? undefined}
                        message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'AI Powered Tools')}
                        onUpgradeCompleted={async () => {
                            updateSubscription();
                        }}
                    />
                );
                return;
            }

            setEnableAI(enabled);
            setAIEnabled(enabled);
        },
        [additionalFeatures, showDialog, sellerMarketplace, updateSubscription, setEnableAI, setAIEnabled]
    );

    // Load scene configuration
    useEffect(() => {
        if (sceneEditor && zkScene) {
            try {
                const config = sceneEditor.getSceneConfig();
                console.log('🔄 sceneConfig:', config);

                // Load current values from backbone model
                if (config) {
                    // Environment - leggi la texture direttamente dal scene
                    setEnvironmentTexture(sceneEditor?.scene?.environmentTexture);

                    // Skybox - leggi la texture dalla configurazione
                    const environmentConfig = config.get?.('environment');
                    const skyboxConfigFromEnv = environmentConfig?.get?.('skybox');
                    const hasSkyboxEnabled = skyboxConfigFromEnv?.get?.('createSkybox') === true;
                    const skyboxFiles = skyboxConfigFromEnv?.get?.('files');

                    // Se la configurazione indica che la skybox è abilitata e ci sono file
                    if (hasSkyboxEnabled && skyboxFiles && skyboxFiles.length > 0) {
                        setSkyboxTexture(skyboxFiles);
                    } else {
                        setSkyboxTexture(null);
                    }

                    // Skybox blur - leggi dal config se disponibile
                    const skyboxBlurConfig = skyboxConfigFromEnv || config.get?.('skybox');
                    if (skyboxBlurConfig) {
                        const blur = skyboxBlurConfig.get?.('blur') ?? 0;
                        setSkyboxBlur(blur * 100);
                    }

                    // Info Points
                    setInfoPointsEnabled(config.get('infoPoints')?.get('enabled') ?? true);
                    const infoPointsConfig = config.get('infoPoints')?.get('pointsStyle');
                    if (infoPointsConfig) {
                        setInfoPointsColor(infoPointsConfig.get?.('color') || '#ffffff');
                        setInfoPointsBorderColor(infoPointsConfig.get?.('borderColor') || '#ffffff');
                        setInfoPointsHighlightColor(infoPointsConfig.get?.('highlightColor') || '#ff0000');
                        setInfoPointsInnerPointColor(infoPointsConfig.get?.('innerPointColor') || '#000000');
                    }

                    // Antialias - usando la nuova logica
                    const antialiasConfig = getAntiAliasConfig();
                    if (antialiasConfig) {
                        // Il parent è abilitato se almeno uno dei device è abilitato
                        const parentEnabled = antialiasConfig.desktop?.enabled || antialiasConfig.mobile?.enabled;
                        setEnableAntialiasing(parentEnabled);

                        setAntialiasDesktop(antialiasConfig.desktop?.enabled || false);
                        setAntialiasDesktopInRotation(antialiasConfig.desktop?.inRotationEnabled || false);
                        setAntialiasMobile(antialiasConfig.mobile?.enabled || false);
                        setAntialiasMobileInRotation(antialiasConfig.mobile?.inRotationEnabled || false);
                    }

                    // WebP Compression - usando zkScene
                    setEnableWebP(zkScene?.getWebPEnabled?.() ?? false);
                    setWebpCompressionType(zkScene?.getWebPType?.() ?? 'Lossy');
                    setWebpCompressionQuality(zkScene?.getWebPQuality?.() ?? 80);

                    // Altri settings usando sceneEditor
                    setOptimizeTextureManagement(sceneEditor?.getMatchSameNameTextures?.() ?? false);
                    setUseDelayedMaterialsLoading(sceneEditor?.getUseDelayedMaterialLoading?.() ?? false);

                    // Other settings dal config
                    // setDracoCompression(zkScene.getForceDracoCompression() === true);
                    setEnableAI(isAIEnabled ?? true);
                }
            } catch (error) {
                console.log('⚠️ Error loading scene config:', error);
            }
        }
    }, [sceneEditor, zkScene, getAntiAliasConfig]);

    // Close color picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openColorPicker && !(event.target as Element)?.closest('.color-picker-container')) {
                setOpenColorPicker(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openColorPicker]);

    const handleSkyboxBlur = useCallback(
        (value: number) => {
            setSkyboxBlur(value);
            // Il blur deve essere normalizzato da 0-100 a 0-1 per setViewerSkyboxBlur
            const normalizedValue = value / 100;
            sceneEditor?.setViewerSkyboxBlur(normalizedValue);
        },
        [sceneEditor]
    );

    const handleInfoPointsColorChange = useCallback(
        (colorType: 'borderColor' | 'color' | 'highlightColor' | 'innerPointColor', color: string) => {
            switch (colorType) {
                case 'color':
                    setInfoPointsColor(color);
                    sceneEditor?.setInfoPointsColor?.(color);
                    break;
                case 'borderColor':
                    setInfoPointsBorderColor(color);
                    sceneEditor?.setInfoPointsBorderColor?.(color);
                    break;
                case 'color':
                    // This seems to be a legacy property - might not be needed
                    break;
                case 'highlightColor':
                    setInfoPointsHighlightColor(color);
                    sceneEditor?.setInfoPointsHighlightColor?.(color);
                    break;
                case 'innerPointColor':
                    setInfoPointsInnerPointColor(color);
                    sceneEditor?.setInfoPointsInnerPointColor?.(color);
                    break;
            }

            // Close the color picker after selection
            // setOpenColorPicker(null);
        },
        [sceneEditor]
    );

    const handleEnableAntialiasing = useCallback(
        (enabled: boolean) => {
            setEnableAntialiasing(enabled);

            // Se disabilito il parent, disabilito anche tutti i figli
            if (!enabled) {
                setAntialiasDesktop(false);
                setAntialiasMobile(false);
                setAntialiasDesktopInRotation(false);
                setAntialiasMobileInRotation(false);
            }

            // Usa la nuova logica per aggiornare l'antialias config
            const currentConfig = getAntiAliasConfig();
            const newConfig = {
                ...currentConfig,
                desktop: {
                    enabled: enabled ? currentConfig.desktop?.enabled || false : false,
                    inRotationEnabled: enabled ? currentConfig.desktop?.inRotationEnabled || false : false
                },
                mobile: {
                    enabled: enabled ? currentConfig.mobile?.enabled || false : false,
                    inRotationEnabled: enabled ? currentConfig.mobile?.inRotationEnabled || false : false
                }
            };

            setAntiAliasConfig(newConfig);
        },
        [sceneEditor, getAntiAliasConfig, setAntiAliasConfig]
    );

    const handleAntialiasChange = useCallback(
        (device: 'desktop' | 'mobile', setting: 'enabled' | 'inRotation', value: boolean) => {
            // Aggiorna lo stato locale
            if (device === 'desktop') {
                if (setting === 'enabled') {
                    setAntialiasDesktop(value);
                    // Se disabilito desktop, disabilito anche inRotation
                    if (!value) {
                        setAntialiasDesktopInRotation(false);
                    }
                } else {
                    setAntialiasDesktopInRotation(value);
                    // Se abilito inRotation, devo abilitare anche desktop
                    if (value && !antialiasDesktop) {
                        setAntialiasDesktop(true);
                    }
                }
            } else {
                if (setting === 'enabled') {
                    setAntialiasMobile(value);
                    // Se disabilito mobile, disabilito anche inRotation
                    if (!value) {
                        setAntialiasMobileInRotation(false);
                    }
                } else {
                    setAntialiasMobileInRotation(value);
                    // Se abilito inRotation, devo abilitare anche mobile
                    if (value && !antialiasMobile) {
                        setAntialiasMobile(true);
                    }
                }
            }

            // Aggiorna la configurazione usando la nuova logica
            const currentConfig = getAntiAliasConfig();
            const newConfig = { ...currentConfig };

            if (device === 'desktop') {
                if (setting === 'enabled') {
                    newConfig.desktop = {
                        ...newConfig.desktop,
                        enabled: value,
                        inRotationEnabled: value ? newConfig.desktop?.inRotationEnabled || false : false
                    };
                } else {
                    // inRotation
                    newConfig.desktop = {
                        ...newConfig.desktop,
                        enabled: value ? true : newConfig.desktop?.enabled || false,
                        inRotationEnabled: value
                    };
                }
            } else {
                // mobile
                if (setting === 'enabled') {
                    newConfig.mobile = {
                        ...newConfig.mobile,
                        enabled: value,
                        inRotationEnabled: value ? newConfig.mobile?.inRotationEnabled || false : false
                    };
                } else {
                    // inRotation
                    newConfig.mobile = {
                        ...newConfig.mobile,
                        enabled: value ? true : newConfig.mobile?.enabled || false,
                        inRotationEnabled: value
                    };
                }
            }

            setAntiAliasConfig(newConfig);
        },
        [sceneEditor, antialiasDesktop, antialiasMobile, getAntiAliasConfig, setAntiAliasConfig]
    );

    // Helper component for color properties like in material editor
    const ColorProperty: FC<{
        id: string;
        label: string;
        value: string;
        onChange: (color: string) => void;
        tooltip?: string;
    }> = ({ id, label, value, onChange, tooltip }) => {
        const isOpen = openColorPicker === id;

        return (
            <Column>
                <Row>
                    <Label>{label}</Label>
                    <ColorPreviewContainer>
                        <ColorPreview color={value} onClick={() => setOpenColorPicker(id)}>
                            {isOpen && (
                                <ColorPickerPopover className='color-picker-container'>
                                    <ColorPicker color={value} onChange={onChange} />
                                </ColorPickerPopover>
                            )}
                        </ColorPreview>
                        <ColorHex onClick={() => setOpenColorPicker(id)}>{value}</ColorHex>
                    </ColorPreviewContainer>
                </Row>
            </Column>
        );
    };

    return (
        <Container>
            {/* Environment Section */}
            <Section>
                <SectionTitle>{T._('Environment', 'Admin')}</SectionTitle>

                <Column>
                    <Label>{T._('Environment Texture', 'Admin')}</Label>
                    <ButtonGroup>
                        <Button size='small' onClick={handleEnvironmentTextureUpload} disabled={isEnvironmentTextureUploading}>
                            {isEnvironmentTextureUploading ? T._('Uploading...', 'Admin') : T._('Upload', 'Admin')}
                        </Button>
                        {environmentTexture && (
                            <Button size='small' onClick={handleEnvironmentTextureRemove}>
                                {T._('Remove', 'Admin')}
                            </Button>
                        )}
                    </ButtonGroup>
                </Column>

                {environmentTexture && <PreviewContainer>{getPreview(getTextureUrls(environmentTexture))}</PreviewContainer>}

                {/* Skybox */}

                <Column>
                    <Label>{T._('Skybox Texture', 'Admin')}</Label>
                    <ButtonGroup>
                        <Button size='small' onClick={handleSkyboxTextureUpload} disabled={isSkyboxTextureUploading}>
                            {isSkyboxTextureUploading ? T._('Uploading...', 'Admin') : T._('Upload', 'Admin')}
                        </Button>
                        {skyboxTexture && (
                            <Button size='small' onClick={handleSkyboxTextureRemove}>
                                {T._('Remove', 'Admin')}
                            </Button>
                        )}
                    </ButtonGroup>
                </Column>

                {skyboxTexture && <PreviewContainer>{getPreview(getTextureUrls(skyboxTexture))}</PreviewContainer>}

                {/* Skybox Blur - Solo se skybox è caricata */}
                {skyboxTexture && (
                    <Column>
                        <Label>{T._('Skybox Blur', 'Admin')}</Label>
                        <SliderContainer>
                            <Slider min={0} max={100} step={1} value={skyboxBlur} onChange={(val) => handleSkyboxBlur(Number(val))} />
                            <SliderValueDisplay>{skyboxBlur.toFixed(1)}</SliderValueDisplay>
                        </SliderContainer>
                    </Column>
                )}
            </Section>

            {/* Antialias Section */}
            <Section>
                <SectionTitle>{T._('Antialias', 'Admin')}</SectionTitle>

                {/* Parent Enable Antialias Checkbox */}
                <Column>
                    <FormControl>
                        <Checkbox label={T._('Enable Anti aliasing', 'Admin')} checked={enableAntialiasing} onChange={handleEnableAntialiasing} />
                    </FormControl>
                </Column>

                {/* Desktop Antialias - Level 2 */}
                {enableAntialiasing && (
                    <IndentedColumn>
                        <FormControl>
                            <Checkbox
                                label={T._('Desktop Enabled', 'Admin')}
                                checked={antialiasDesktop}
                                onChange={(checked) => handleAntialiasChange('desktop', 'enabled', checked)}
                            />
                        </FormControl>
                    </IndentedColumn>
                )}

                {/* Desktop In Rotation - Level 3 */}
                {enableAntialiasing && antialiasDesktop && (
                    <DoubleIndentedColumn>
                        <FormControl>
                            <Checkbox
                                label={T._('Desktop In Rotation', 'Admin')}
                                checked={antialiasDesktopInRotation}
                                onChange={(checked) => handleAntialiasChange('desktop', 'inRotation', checked)}
                            />
                        </FormControl>
                    </DoubleIndentedColumn>
                )}

                {/* Mobile Antialias - Level 2 */}
                {enableAntialiasing && (
                    <IndentedColumn>
                        <FormControl>
                            <Checkbox
                                label={T._('Mobile Enabled', 'Admin')}
                                checked={antialiasMobile}
                                onChange={(checked) => handleAntialiasChange('mobile', 'enabled', checked)}
                            />
                        </FormControl>
                    </IndentedColumn>
                )}

                {/* Mobile In Rotation - Level 3 */}
                {enableAntialiasing && antialiasMobile && (
                    <DoubleIndentedColumn>
                        <FormControl>
                            <Checkbox
                                label={T._('Mobile In Rotation', 'Admin')}
                                checked={antialiasMobileInRotation}
                                onChange={(checked) => handleAntialiasChange('mobile', 'inRotation', checked)}
                            />
                        </FormControl>
                    </DoubleIndentedColumn>
                )}
            </Section>

            {/* Info Points Section */}
            <Section>
                <SectionTitle>{T._('Info Points', 'Admin')}</SectionTitle>

                <Column>
                    <FormControl>
                        <Checkbox
                            label={T._('Info Points', 'Admin')}
                            checked={infoPointsEnabled}
                            onChange={setInfoPointsEnabledHandler}
                            helpTooltip={
                                <>
                                    {T._('Enable your customers to see high-res 2D images of the product on 3D model. ', 'Composer')}
                                    <br />
                                    <a
                                        style={{ color: '#f78200', textDecoration: 'underline' }}
                                        href='https://zakeke.zendesk.com/hc/en-us/articles/360014772579'
                                        target='_blank'
                                    >
                                        {T._('Learn more', 'Composer')}
                                    </a>
                                </>
                            }
                        />
                    </FormControl>
                </Column>

                {infoPointsEnabled && (
                    <>
                        <ColorProperty
                            id='color'
                            label={T._('Color', 'Admin')}
                            value={infoPointsColor}
                            onChange={(color) => handleInfoPointsColorChange('color', color)}
                        />
                        <ColorProperty
                            id='borderColor'
                            label={T._('Border Color', 'Admin')}
                            value={infoPointsBorderColor}
                            onChange={(color) => handleInfoPointsColorChange('borderColor', color)}
                        />

                        <ColorProperty
                            id='highlightColor'
                            label={T._('Highlight Color', 'Admin')}
                            value={infoPointsHighlightColor}
                            onChange={(color) => handleInfoPointsColorChange('highlightColor', color)}
                        />

                        <ColorProperty
                            id='innerPointColor'
                            label={T._('Inner Point Color', 'Admin')}
                            value={infoPointsInnerPointColor}
                            onChange={(color) => handleInfoPointsColorChange('innerPointColor', color)}
                        />
                    </>
                )}
            </Section>

            {/* Other Settings Section */}
            <Section>
                <SectionTitle>{T._('Other Settings', 'Admin')}</SectionTitle>

                {/* Optimize Texture Management */}
                <Column>
                    <FormControl>
                        <Checkbox
                            label={T._('Optimize Texture Management', 'Admin')}
                            checked={optimizeTextureManagement}
                            onChange={setMatchSameNameTextures}
                            helpTooltip={T._(
                                'Effortlessly merge textures with identical names into a single texture to optimize performance. When you upload the textures with the same names on multiple materials, Zakeke automatically consolidates them, allowing the browser to download it just once. This optimization minimizes loading times and boosts overall performance, providing a faster and smoother 3D product experience for your customers.',
                                'Admin'
                            )}
                        />
                    </FormControl>
                </Column>

                {/* Enable WebP for Textures */}
                <Column>
                    <FormControl>
                        <Checkbox
                            label={T._('Enable WebP for Textures', 'Admin')}
                            checked={enableWebP}
                            onChange={setWebPEnabled}
                            helpTooltip={
                                <span>
                                    {T._('Enable WebP to optimize texture file sizes. You have two options: ', 'Composer')}
                                    <div>
                                        <strong>{T._('• Lossy: ', 'Composer')}</strong>
                                        {T._('Heavily compresses textures with some quality loss. You can customize the quality level.', 'Composer')}
                                    </div>
                                    <div>
                                        <strong>{T._('• Lossless: ', 'Composer')}</strong>
                                        {T._('Minimally compresses textures to maintain original quality.', 'Composer')}
                                    </div>
                                    <a
                                        target='_blank'
                                        style={{ color: '#f78200', textDecoration: 'underline' }}
                                        href='https://zakeke.zendesk.com/hc/en-us/articles/14248548502940-WebP-for-Textures'
                                    >
                                        {' '}
                                        We recommend delving deeper here.
                                    </a>
                                </span>
                            }
                        />
                    </FormControl>
                </Column>

                {enableWebP && (
                    <>
                        <Column>
                            <Label>{T._('Compression Type', 'Admin')}</Label>
                            <AdvancedSelect
                                value={{ value: webpCompressionType, label: T._(webpCompressionType, 'Admin') }}
                                options={[
                                    { label: T._('Lossy', 'Admin'), value: 'Lossy' },
                                    { label: T._('Lossless', 'Admin'), value: 'Lossless' }
                                ]}
                                onChange={(value) => value && setWebPType(value.value)}
                            />
                        </Column>
                        {webpCompressionType === 'Lossy' && (
                            <Column>
                                <Label>{T._('Quality', 'Admin')}</Label>
                                <Slider min={1} max={100} value={webpCompressionQuality} onChange={(val) => setWebPQuality(Number(val))} />
                            </Column>
                        )}
                    </>
                )}

                {/* Use delayed materials loading */}
                <Column>
                    <FormControl>
                        <Checkbox
                            label={T._('Use delayed materials loading', 'Admin')}
                            checked={useDelayedMaterialsLoading}
                            onChange={setUseDelayedMaterialLoading}
                        />
                    </FormControl>
                </Column>
                <Column>
                    <FormControl>
                        <Checkbox
                            label={T._('AI Product Configuration Generator', 'Admin')}
                            checked={enableAI}
                            onChange={(checked) => {
                                setAiConfigurationEnabled(checked);
                            }}
                            helpTooltip={
                                <>
                                    {T._(
                                        "Enhance your product customization experience with our AI-Powered Configuration Generator. Your clients simply describe their desired configuration and our advanced AI technology automatically generates the perfect customized product for them. No manual option selection, our intelligent system handles the permutations and delivers a tailored configuration that precisely matches your customers' preferences.",
                                        'Composer'
                                    )}
                                    <br />
                                    <a
                                        target='_blank'
                                        style={{ color: '#f78200', textDecoration: 'underline' }}
                                        href='https://zakeke.zendesk.com/hc/en-us/articles/9649789714460-AI-Configuration'
                                    >
                                        {T._('Learn more', 'Composer')}
                                    </a>
                                </>
                            }
                        />
                    </FormControl>
                </Column>

                {/* <Column>
					<FormControl>
						<Checkbox
							label={T._('Draco Compression', 'Admin')}
							checked={dracoCompression}
							onChange={(checked) => {
								setDracoCompression(checked);
								try {
									sceneEditor.setForceDracoCompression(checked);
								} catch (error) {
									console.error('Error updating draco compression:', error);
								}
							}}
							helpTooltip={T._('Enable Draco geometry compression', 'Admin')}
						/>
					</FormControl>
				</Column> */}
            </Section>
        </Container>
    );
};

export default SettingsEditor;
